import React,{Component} from 'react';
import PropTypes from 'prop-types';
import {
  FormGroup,
  FormText,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Popover,
  PopoverBody
} from 'reactstrap';
import Dropzone from 'react-dropzone';
import Cropper from 'react-image-crop';

import ProgressEllipses from '../../../shared/components/ProgressEllipses';

import { uploadImageWithCrop, uploadVideo, generateGUID  } from '../../../shared/utils';

import CloseIconSVG from '../../../shared/assets/close.svg';
import InformationSVG from '../../../shared/assets/information.svg';

const MEGABYTE = 1024 * 1024,
  MAX_VIDEO_SIZE = 10 * MEGABYTE,
  MAX_IMAGE_SIZE = 2 * MEGABYTE,
  VIDEO_FILE_TYPE = 'video',
  IMAGE_FILE_TYPE = 'image',
  ALLOWED_VIDEO_FILE_EXTENSIONS = ['mp4','mov'],
  ALLOWED_IMAGE_FILE_EXTENSIONS = ['jpg','jpeg','png'];

class DroppableFileInput extends Component {
  constructor(){
    super();

    this.state = {
      randomId:generateGUID(),
      cropperOpen:false,
      tooltipOpen:false,
      error:null,
      localFile:null,
      localFileURL:null,
      existingFile:null,
      localCroppedFileURL:null,
      uploading:false,
      crop:{width:0,height:0,x:0,y:0},
      size:{w:0,h:0}
    };

    this.handleKeyPressed = this.handleKeyPressed.bind(this);
  }

  componentDidMount(){
    document.addEventListener('keydown',this.handleKeyPressed);
    
    if(this.props.existingFileName && this.props.existingFileURL){
      this.setState({
        existingFile:{
          name:this.props.existingFileName,
          url:this.props.existingFileURL
        }
      });
    }
  }

  componentWillUnmount(){
    document.removeEventListener('keydown',this.handleKeyPressed);
  }

  handleKeyPressed(event){
    if(this.state.cropperOpen && event.key === 'Enter'){
      if(this.state.crop.width === 0 || this.state.crop.height === 0){
        return;
      } else {
        this.handleCroppedImageSubmit();
      }
    }
  }


  handleDrop(files){
    const localFile = files[0];
    const { fileType } = this.props;

    if(!localFile) return;

    const fileExtension = localFile.name.slice(localFile.name.lastIndexOf('.') + 1).toLowerCase();

    if(fileType === VIDEO_FILE_TYPE && ALLOWED_VIDEO_FILE_EXTENSIONS.indexOf(fileExtension) < 0){
      this.handleErr(new Error('Video file must be of type mp4 or mov'));
      return;
    }

    if(fileType === IMAGE_FILE_TYPE && ALLOWED_IMAGE_FILE_EXTENSIONS.indexOf(fileExtension) < 0){
      this.handleErr(new Error('Image file must be of type jpeg or png'));
      return;
    }


    if(fileType === VIDEO_FILE_TYPE && localFile.size > MAX_VIDEO_SIZE){
      this.handleErr(new Error('Video is too large, please use one under 10mb'));
      return;
    }

    if(fileType === IMAGE_FILE_TYPE && localFile.size > MAX_IMAGE_SIZE){
      this.handleErr(new Error('Image is too large, please use one under 2mb'));
      return;
    }

    if(fileType === VIDEO_FILE_TYPE){
      this.setState({ 
        localFile:localFile, 
        uploading:true, 
        error:null 
      });
      uploadVideo(localFile)
        .then((res) => this.handleUpload(res))
        .catch((err) => this.handleErr(err))
    } else {
      this.setState({
        cropperOpen:true,
        error:null,
        localFile:localFile,
        localFileURL:URL.createObjectURL(localFile)
      });
    }
  }

  handleErr(err){
    console.log('UPLOADER GOT THE FOLLOWING ERROR -> ',err)
    if((err instanceof TypeError) && err.message.indexOf('Failed to fetch') >= 0){
      this.setState({
        error:new Error('A network issue has occurred.  Please try again later.'),
        uploading:false
      });
    } else {
      this.setState({
        error:err,
        uploading:false
      });
    }
  }

  handleUpload(res){
    if(res.thumbnail){
      this.setState({localCroppedFileURL:res.thumbnail});
    }
    this.setState({uploading:false});
    this.props.onChange({ ...res, fileName:this.state.localFile.name });
  }

  handleImageLoad(image){
    const aspectRatio = this.props.cropperAspectRatio;

    let cropWidth, cropHeight;

    if((image.naturalHeight * aspectRatio) > image.naturalWidth){
      cropWidth = image.naturalWidth;
      cropHeight = image.naturalWidth / aspectRatio;
    } else {
      cropHeight = image.naturalHeight;
      cropWidth = aspectRatio * image.naturalHeight;
    }

    const initialCrop = {
      x:0,
      y:0,
      width:(cropWidth / image.naturalWidth) * 100,
      height:(cropHeight / image.naturalHeight) * 100,
      aspect:aspectRatio
    };

    this.setState({
      crop:initialCrop,
      size:{
        width:image.naturalWidth,
        height:image.naturalHeight
      }
    });
  }

  handleCroppedImageSubmit(){
    const { crop, size, localFile } = this.state;

    const finalCrop = {
      x: Math.floor(crop.x * size.width * 0.01, 10),
      y: Math.floor(crop.y * size.height * 0.01, 10),
      width: Math.floor(crop.width * size.width * 0.01, 10),
      height: Math.floor(crop.height * size.height * 0.01, 10),
      aspectRatio: this.props.cropperAspectRatio
    };

    this.setState({
      cropperOpen:false,
      uploading:true
    });

    uploadImageWithCrop(localFile,finalCrop)
      .then((res) => this.handleUpload(res))
      .catch((err) => this.handleErr(err))
  }

  handleClearClick(){
    this.setState({
      localFile:null,
      localFileURL:null,
      localCroppedFileURL:null,
      error:null,
      existingFile:null
    });

    this.props.onChange({
      id:'',
      fileName:'',
      original:'',
      large:'',
      uncropped:'',
      medium:'',
      thumbnail:'',
      small:''
    });
  }

  getPreviewURL(){
    const { existingFile, localCroppedFileURL, localFile } = this.state;

    if(existingFile) return existingFile.url;
    if(localCroppedFileURL) return localCroppedFileURL;
    if(localFile) return URL.createObjectURL(localFile);
    return null;
  }

  getPreviewFileName(){
    const { existingFile, localFile } = this.state;

    if(existingFile) return existingFile.name;
    if(localFile) return localFile.name;
    return null;
  }

  render(){
    const { 
      warning,
      text,
      label,
      fileType, 
      tooltip,
      additionalOptions,
      disabled
    } = this.props;

    const { 
      cropperOpen, 
      tooltipOpen,
      error, 
      uploading, 
      localFile, 
      crop, 
      localFileURL, 
      existingFile, 
      randomId  
    } = this.state;

    const feedback = error ? error.message : warning;

    const previewFileName = this.getPreviewFileName();

    return (
      <span className={`DroppableFileInput ${disabled ? 'DisabledDroppableFileInput' :''}`}>
        <Modal
          isOpen={cropperOpen}
          toggle={() => this.setState({cropperOpen:!cropperOpen,localFile:null})}
        >
          <ModalHeader>Crop to fit</ModalHeader>
          <ModalBody>
            {localFileURL && (
              <Cropper
                src={localFileURL}
                crop={crop}
                onChange={(crop) => this.setState({crop:crop})}
                onImageLoaded={(image) => this.handleImageLoad(image)}
                width='500'
              />
            )}
          </ModalBody>
          <ModalFooter>
            <Button 
              color='primary'
              onClick={() => this.handleCroppedImageSubmit()}
            >
              Crop and upload
            </Button>
            <Button onClick={() => this.setState({cropperOpen:false,localFile:null})}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
        <FormGroup id={this.props.id}>
          {label.length > 0 && (
            <span>
              <Label>{label}</Label>
              {tooltip && (
                <span>
                  &nbsp;&nbsp;
                  <img
                    alt='Aspect ratio information'
                    src={InformationSVG}
                    id={`tooltip_${randomId}`}
                    onMouseEnter={() => this.setState({tooltipOpen:true})}
                    onMouseLeave={() => this.setState({tooltipOpen:false})}
                  />
                  <Popover
                    className='TooltipPopover'
                    isOpen={tooltipOpen}
                    target={`tooltip_${randomId}`}
                    placement='right'
                  >
                    <PopoverBody>{tooltip}</PopoverBody>
                  </Popover>
                </span>
              )}
            </span>
          )}
          {additionalOptions && (
            <div style={{marginBottom:'0.5rem'}}>
              {additionalOptions}
            </div>
          )}
          {((!uploading && !localFile && !existingFile)  || disabled )&& (
            <Dropzone
              className={`DropTarget ${feedback.length > 0 ? 'form-control-danger' : ''}`}
              multiple={false}
              onDrop={(files) => this.handleDrop(files)}
              disabled={disabled}
            >
              {text}
            </Dropzone>
          )}
          {(((localFile || existingFile) && fileType === VIDEO_FILE_TYPE) && !disabled) && (
            <div className='UploadPreview'>
              <div className='VideoContainer'>
                <video height='auto' width='74'>
                  <source src={this.getPreviewURL()} />
                </video>
              </div>
              {uploading ? (
                <span>
                  <span className='UploadingIndicator'>
                    &nbsp;&nbsp;&nbsp;&nbsp;Uploading<ProgressEllipses />
                  </span>
                </span>
              ) : (
                <span>
                  <span className='FileName'>
                    &nbsp;&nbsp;&nbsp;&nbsp;{previewFileName}
                  </span>
                  <img
                    alt='Close'
                    className='float-right CloseIcon'
                    src={CloseIconSVG}
                    onClick={() => this.handleClearClick()}
                  />
                </span>
              )}
            </div>
          )}
          {((localFile || existingFile) && fileType === IMAGE_FILE_TYPE) && !disabled && (
            <div className='UploadPreview'>
              <img 
                alt='Preview'
                className='PreviewThumbnail' 
                src={this.getPreviewURL()} 
              />
              {uploading ? (
                <span>
                  <span className='UploadingIndicator'>
                    &nbsp;&nbsp;&nbsp;&nbsp;Uploading<ProgressEllipses />
                  </span>
                </span>
              ) : (
                <span>
                  <div className='FileName'>
                    &nbsp;&nbsp;&nbsp;&nbsp;{previewFileName}
                  </div>
                  <img
                    alt='Close'
                    className='float-right CloseIcon'
                    src={CloseIconSVG}
                    onClick={() => this.handleClearClick()}
                  />
                </span>
              )}
            </div>
          )}
          {feedback.length > 0 && (
            <FormText className='DangerFeedback'>{feedback}</FormText>
          )}
        </FormGroup>
      </span>
    )
  }
}

DroppableFileInput.propTypes = {
  warning:PropTypes.string,
  label:PropTypes.string,
  fileType:PropTypes.oneOf([IMAGE_FILE_TYPE,VIDEO_FILE_TYPE]),
  onChange:PropTypes.func,
  cropperAspectRatio:PropTypes.number,
  existingFileName:PropTypes.string,
  existingFileURL:PropTypes.string,
  tooltip:PropTypes.any,
  additionalOptions:PropTypes.any
};

DroppableFileInput.defaultProps = {
  warning:'',
  label:'',
  disabled:false,
  fileType:'image',
  onChange:() => {},
  cropperAspectRatio:4/3
};

export default DroppableFileInput;



// WEBPACK FOOTER //
// ./src/shared/components/DroppableFileInput/DroppableFileInput.js